import { useEffect, useState } from 'react';
import { Box, Fade, Paper, Alert, useMediaQuery, useTheme } from '@mui/material';
import { FullPaymentScheduleList, FullPaymentScheduleTable } from '@acorn-portal/shared/src/components/paymentDetails';
import { PageTitle } from '@acorn-portal/shared/src/components/text';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { getBillingSchedule } from '@acorn-portal/shared/src/store/managedPayments.slice';
import { BillingScheduleCollectionResponse } from '@acorn-portal/shared/src/services/managedPaymentsApi';
import API_POLICY_TOKEN from '@acorn-portal/shared/src/services/managedPaymentsInstance';
import BackButton from '../../components/buttons/BackButton';
import ActionButtons from '@acorn-portal/shared/src/components/wrapper/action-buttons';
import { useNavigate } from 'react-router';
import FullPageLoadingSplash from '../../components/loading/FullPageLoadingSplash';

const FullPaymentSchedule = () => {
	const dispatch = useAppDispatch();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const navigate = useNavigate();
	const policyToken = useAppSelector((state) => state.fullPolicy.policyToken);

	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [collections, setCollections] = useState<BillingScheduleCollectionResponse[]>([]);

	useEffect(() => {
		let active = true;

		const getBillingDetails = async () => {
			try {
				if (!policyToken) {
					if (active) {
						setError('Failed to fetch collections. Please try again later.');
						setLoading(false);
					}
					return;
				}

				setLoading(true);
				API_POLICY_TOKEN.defaults.headers.common['X-Token'] = policyToken;
				const billingSchedule = await dispatch(getBillingSchedule()).unwrap();

				if (!billingSchedule) return [] as BillingScheduleCollectionResponse[];

				if (!billingSchedule?.schedules) return;

				if (!billingSchedule.schedules[0].collections) return;

				if (active) {
					setCollections(billingSchedule.schedules[0].collections || []);
				}
			} catch (err) {
				console.error('Error fetching billing schedule:', err);
				if (active) {
					setError('Failed to fetch collections. Please try again later.');
				}
			} finally {
				if (active) {
					setLoading(false);
				}
			}
		};

		getBillingDetails();
		return () => {
			active = false;
		};
	}, [dispatch, policyToken]);

	return (
		<FullPageLoadingSplash loading={loading} message={'Loading Payment Schedule...'}>
			<Paper
				elevation={1}
				sx={{
					borderRadius: '8px',
				}}
			>
				<Fade in>
					<Box
						sx={{
							my: '1rem',
							mx: { sm: 0, md: '1rem' },
							textAlign: { xs: 'left', md: 'center' },
							p: 1,
						}}
					>
						<PageTitle>Payment Schedule</PageTitle>

						{error && <Alert severity='error'>{error}</Alert>}
						{!loading && !error && isMobile && <FullPaymentScheduleList collections={collections} />}
						{!loading && !error && !isMobile && <FullPaymentScheduleTable collections={collections} />}
						<Box sx={{ m: 2 }}>
							<ActionButtons>
								<BackButton onClick={() => navigate(-1)}>Back</BackButton>
							</ActionButtons>
						</Box>
					</Box>
				</Fade>
			</Paper>
		</FullPageLoadingSplash>
	);
};

export default FullPaymentSchedule;
